/** Largest delay `setTimeout` honours (signed 32-bit ms); a bigger one fires immediately. */
export const MAX_TIMER_MS = 2_147_483_647;

/** Never renew sooner than this when the token is about to expire anyway. */
const FLOOR_MS = 5_000;
/** First retry delay when the BE reply carries no usable `expiresIn`. */
const UNUSABLE_BASE_MS = 60_000;
/** Backoff ceiling for repeated degenerate replies. */
const BACKOFF_CAP_MS = 15 * 60_000;

export interface RenewDelay {
  delayMs: number;
  /** Consecutive degenerate replies so far; feed it back into the next call. */
  streak: number;
}

/**
 * When to silently re-handshake after a reply with `expiresIn` seconds.
 *
 * Normal case: `leewaySeconds` before expiry. A missing / non-finite / non-positive `expiresIn`
 * used to produce `NaN` (and a 0 ms timer, so an endless immediate re-handshake loop), a token that
 * lives less than the leeway re-fired every 5 s, and a lifetime past ~24.8 days overflowed the timer
 * and fired at once. Degenerate replies now back off exponentially; `streak` carries the count.
 */
export function computeRenewDelay(
  expiresIn: unknown,
  leewaySeconds: number,
  streak: number,
): RenewDelay {
  // `ceiling` bounds the backoff: a known lifetime must never be outwaited (the token would expire
  // before the renew), but never below FLOOR_MS (no tight loop).
  const backoff = (base: number, ceiling = BACKOFF_CAP_MS): RenewDelay => ({
    delayMs: Math.min(ceiling, base * 2 ** Math.min(streak, 10)),
    streak: streak + 1,
  });
  if (typeof expiresIn !== "number" || !Number.isFinite(expiresIn) || expiresIn <= 0) {
    return backoff(UNUSABLE_BASE_MS);
  }
  const ms = (expiresIn - leewaySeconds) * 1000;
  if (ms < FLOOR_MS) {
    return backoff(
      FLOOR_MS,
      Math.min(BACKOFF_CAP_MS, Math.max(FLOOR_MS, expiresIn * 1000 - FLOOR_MS)),
    );
  }
  return { delayMs: Math.min(MAX_TIMER_MS, ms), streak: 0 };
}
